/**
 * How a report parameter is edited and what it sends.
 *
 * Kept apart from ReportRunner.vue so the rules stay small and readable. The
 * server never sees a picker's internal shape: whatever the control holds, the
 * request carries the same plain string or number a text box would have sent, so
 * report classes did not change when their boxes became lookups.
 */

/**
 * Whether a parameter is rendered as a lookup picker rather than a plain input:
 * anything with a server list (optionsSource) or inline options, except radio
 * groups, which keep their own layout.
 */
export function isPicker(param) {
    if (param.type === 'radio') return false
    return !!param.optionsSource || Array.isArray(param.options)
}

export function joinOf(param) {
    return param.join !== undefined && param.join !== null ? param.join : ','
}

/**
 * Relative date defaults, resolved in the browser's local date when the form
 * opens and on reset, the way FrameTracker's FormShow sets them (dBeg.Date :=
 * Date - 31 is 'today-31'; EncodeDate(Year, 1, 1) is 'yearStart').
 *
 * A token is <base>[<+|-><N><unit>]... (contract addendum 1):
 *   bases:   today, monthStart, yearStart (monthEnd and yearEnd also accepted)
 *   units:   d days (the default: 'today-31'), m months ('monthStart-1m'),
 *            y years ('yearStart-1y'); offsets chain ('yearStart-1d' is
 *            31 December last year)
 * Months step like Delphi's IncMonth: the day is kept and clamped to the end of
 * a shorter month (March 31 minus a month is February 28).
 *
 * Returns 'YYYY-MM-DD', or null when the string is not a token (a literal date
 * default is then used as it is).
 */
const DATE_TOKEN = /^(today|monthStart|monthEnd|yearStart|yearEnd)((?:[+-]\d+[dmy]?)*)$/

export function resolveDateToken(token, now = new Date()) {
    const match = DATE_TOKEN.exec(String(token).trim())
    if (!match) return null

    let y = now.getFullYear()
    let m = now.getMonth()
    let d = now.getDate()

    if (match[1] === 'monthStart') d = 1
    if (match[1] === 'monthEnd') d = daysIn(y, m)
    if (match[1] === 'yearStart') { m = 0; d = 1 }
    if (match[1] === 'yearEnd') { m = 11; d = 31 }

    const offsets = match[2].match(/[+-]\d+[dmy]?/g) || []
    offsets.forEach(offset => {
        const unit = /[dmy]$/.test(offset) ? offset.slice(-1) : 'd'
        const n = parseInt(offset, 10)
        if (unit === 'd') {
            const date = new Date(y, m, d + n)
            y = date.getFullYear()
            m = date.getMonth()
            d = date.getDate()
        } else {
            const months = unit === 'y' ? n * 12 : n
            const total = y * 12 + m + months
            y = Math.floor(total / 12)
            m = total - y * 12
            d = Math.min(d, daysIn(y, m))
        }
    })

    return y + '-' + String(m + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0')
}

function daysIn(year, month) {
    return new Date(year, month + 1, 0).getDate()
}

/**
 * The form's starting value. A multiple picker holds an array; a declared default
 * is split on its separator ('' splits "BCE" into letters). A search picker holds
 * the chosen option object, so its label survives the next search; a default is
 * seeded with the value as its own label until the server resolves it.
 *
 * `context.user` is the catalog's meta.user: `defaultFrom: 'userLocation'` starts
 * the param on the signed-in user's location, as FrameTracker presets Sys.Loc,
 * and `defaultFrom: 'userSalesCode'` on the user's sales code (FormShow presets
 * a sales-coded operator's own code); either is blank when the user has none.
 * A checkbox starts on its default, else unchecked. `context.now` fixes
 * "today" for tests.
 */
export function defaultFor(param, context = {}) {
    let def = param.default !== undefined && param.default !== null ? param.default : ''

    if (param.type === 'checkbox') {
        return String(def !== '' ? def : (param.uncheckedValue !== undefined ? param.uncheckedValue : ''))
    }

    const fromUser = userDefault(param, context)
    if (fromUser !== undefined) def = fromUser === null ? '' : fromUser

    if (param.type === 'date' && typeof def === 'string' && def !== '') {
        const resolved = resolveDateToken(def, context.now || new Date())
        if (resolved !== null) def = resolved
    }

    // 'currentYear' on a number param: 3 Year Sales' read-only year.
    if (param.type === 'number' && def === 'currentYear') def = (context.now || new Date()).getFullYear()

    if (param.uppercase && typeof def === 'string') def = def.toUpperCase()

    if (param.multiple) {
        if (Array.isArray(def)) return def.map(String)
        if (def === '') return []
        return String(def).split(joinOf(param)).filter(v => v !== '')
    }

    // A single picker's "nothing chosen" is null: the multiselect treats '' as a
    // chosen option with an empty label and hides its placeholder.
    if (isPicker(param) && def === '') return null

    if (param.search) return {value: String(def), label: String(def)}

    return def
}

const USER_FIELDS = {userLocation: 'location', userSalesCode: 'salesCode'}

/** The user's value a defaultFrom names: undefined when none is named, null when blank. */
function userDefault(param, context) {
    const field = USER_FIELDS[param.defaultFrom]
    if (!field) return undefined
    const value = (context.user || {})[field]
    return value === undefined || value === null || value === '' ? null : String(value)
}

/**
 * Whether a param is locked on its user default: `lockWhenDefaulted` with a
 * user who has the value. FrameTracker disables the Sls combo for a
 * sales-coded operator; a user without a code gets the normal control. A
 * locked param is still sent.
 */
export function isLocked(param, context = {}) {
    if (!param.lockWhenDefaulted) return false
    const value = userDefault(param, context)
    return value !== undefined && value !== null
}

/** The value a search picker's option object stands for. */
function plain(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value) ? value.value : value
}

/** The plain value(s) a form field holds, for comparing against a condition. */
function plainValues(value) {
    if (Array.isArray(value)) return value.map(plain).filter(v => v !== '' && v !== null && v !== undefined).map(String)
    const v = plain(value)
    return v === '' || v === null || v === undefined ? [] : [String(v)]
}

/**
 * Whether a {param: [values]} condition holds on the live form: every named
 * param must hold one of its values (a multiple pick: any of them). A blank
 * field matches '' in the list. Used by visibleWhen, enabledWhen and
 * requiredWhen, which mirror FrameTracker's OnClick handlers that show, enable
 * or demand a control depending on another.
 */
export function conditionHolds(when, form) {
    if (!when) return true
    // `message` rides along on requiredWhen; it is not a condition.
    return Object.keys(when).filter(name => name !== 'message').every(name => {
        const allowed = (Array.isArray(when[name]) ? when[name] : [when[name]]).map(a => (a === null ? '' : String(a)))
        const held = plainValues(form[name])
        if (!held.length) return allowed.includes('')
        return held.some(v => allowed.includes(v))
    })
}

export function isVisible(param, form) {
    return conditionHolds(param.visibleWhen, form)
}

export function isEnabled(param, form) {
    return !param.readOnly && conditionHolds(param.enabledWhen, form)
}

export function isRequired(param, form) {
    if (param.required) return true
    return !!param.requiredWhen && conditionHolds(param.requiredWhen, form)
}

/**
 * The value submitted for one parameter, or undefined when it is not sent.
 * Blank means "no filter": sending an empty string would fail integer validation
 * on the server rather than being ignored. A multiple picker is joined into ONE
 * string; nothing picked sends nothing. An `uppercase` param (FrameTracker's
 * CharCase = ecUpperCase) is sent in capitals whatever was typed or picked.
 */
export function serialize(param, value) {
    const upper = v => (param.uppercase && typeof v === 'string' ? v.toUpperCase() : v)

    if (param.multiple) {
        const list = (Array.isArray(value) ? value : []).map(plain).filter(v => v !== '' && v !== null && v !== undefined)
        return list.length ? upper(list.join(joinOf(param))) : undefined
    }

    const v = plain(value)
    return v === '' || v === null || v === undefined ? undefined : upper(v)
}

/**
 * The request parameters for a whole form. A param hidden by visibleWhen is not
 * sent at all, as FrameTracker ignores a control it has hidden.
 */
export function serializeForm(params, form) {
    const out = {}
    params.forEach(p => {
        if (!isVisible(p, form)) return
        const v = serialize(p, form[p.name])
        if (v !== undefined) out[p.name] = v
    })
    return out
}

/**
 * Messages for required params left blank, in form order: FrameTracker refuses
 * to print and says why ('Please Select Location'). `requiredMessage` (or
 * requiredWhen's `message`) gives the report's own wording; otherwise
 * "<label> is required.". Hidden params are never required.
 */
export function missingRequired(params, form) {
    return params
        .filter(p => isVisible(p, form) && isRequired(p, form) && serialize(p, form[p.name]) === undefined)
        .map(p => p.requiredMessage || (p.requiredWhen && p.requiredWhen.message) || ((p.label || p.name) + ' is required.'))
}
